import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import {
  Card, CardHeader, CardBody, PageHeader,
  TableWrapper, Table, Th, Td, Badge,
} from '../../components/ui';
import { FaEdit, FaTrash, FaPaperPlane } from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const EVENT_GROUPS = {
  'User Events': ['user.registered', 'user.deleted', 'user.invited', 'user.invite_accepted'],
  'Subscription Events': ['subscription.created', 'subscription.upgraded', 'subscription.cancelled', 'subscription.expired'],
  'Billing Events': ['billing.payment_succeeded', 'billing.payment_failed'],
};

const empty = {
  webhookUrl: '',
  label: '',
  events: [],
  active: true,
  secret: '',
};

export default function AdminWebhooks() {
  const modal = useModal();
  const [webhooks, setWebhooks] = useState([]);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    const res = await apiGet('/api/admin/webhooks');
    setWebhooks(res?.webhooks || []);
  };

  const toggleEvent = (eventKey) => {
    setForm((prev) => ({
      ...prev,
      events: prev.events.includes(eventKey)
        ? prev.events.filter((e) => e !== eventKey)
        : [...prev.events, eventKey],
    }));
  };

  const selectAllGroup = (groupEvents) => {
    setForm((prev) => {
      const allSelected = groupEvents.every((e) => prev.events.includes(e));
      return {
        ...prev,
        events: allSelected
          ? prev.events.filter((e) => !groupEvents.includes(e))
          : [...new Set([...prev.events, ...groupEvents])],
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.events.length) {
      modal.alert('Please select at least one event', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        webhookUrl: form.webhookUrl,
        label: form.label,
        events: form.events,
        active: form.active,
        secret: form.secret,
      };

      let res;
      if (editingId) res = await apiPut(`/api/admin/webhooks/${editingId}`, payload);
      else res = await apiPost('/api/admin/webhooks', payload);

      if (res?.success) {
        setForm(empty);
        setEditingId(null);
        setShowForm(false);
        load();
      } else {
        modal.alert(res?.message || 'Failed to save webhook', 'error');
      }
    } catch (err) {
      modal.alert(err?.message || 'Failed to save webhook', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const del = async (id) => {
    if (!(await modal.confirm('Delete this webhook?', { danger: true, confirmText: 'Delete' }))) return;
    const res = await apiDelete(`/api/admin/webhooks/${id}`);
    if (res?.success) load();
  };

  const edit = (wh) => {
    setEditingId(wh.id);
    setForm({
      webhookUrl: wh.webhookUrl || '',
      label: wh.label || '',
      events: wh.events || [],
      active: wh.active !== false,
      secret: wh.secret || '',
    });
    setShowForm(true);
  };

  const testWebhook = async (wh) => {
    setTesting(wh.id);
    try {
      const res = await apiPost('/api/admin/webhooks/test', {
        webhookUrl: wh.webhookUrl,
        event: 'test',
      });
      if (res?.success) {
        modal.alert(`Test sent successfully (status: ${res.statusCode})`, 'success');
      } else {
        modal.alert(res?.message || 'Test failed', 'error');
      }
    } catch (err) {
      modal.alert(err?.message || 'Test failed', 'error');
    } finally {
      setTesting(null);
    }
  };

  const cancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(empty);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <PageHeader title="Webhooks" subtitle="Send platform events to external services">
        {!showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            Add Webhook
          </button>
        )}
      </PageHeader>

      {showForm && (
        <Card>
          <CardHeader>{editingId ? 'Edit Webhook' : 'Add Webhook'}</CardHeader>
          <CardBody>
            <form onSubmit={submit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="input-group">
                  <label className="input-label">Label</label>
                  <input
                    className="input"
                    placeholder="My CRM webhook"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Webhook URL</label>
                  <input
                    className="input"
                    type="url"
                    placeholder="https://example.com/webhook"
                    value={form.webhookUrl}
                    onChange={(e) => setForm({ ...form, webhookUrl: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="input-label mb-2 block">Events</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(EVENT_GROUPS).map(([group, events]) => (
                    <div key={group} className="border border-surface-border rounded-lg p-3">
                      <label className="flex items-center gap-2 mb-2 font-medium text-sm text-ink-primary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={events.every((e) => form.events.includes(e))}
                          onChange={() => selectAllGroup(events)}
                          className="rounded"
                        />
                        {group}
                      </label>
                      <div className="space-y-1 pl-5">
                        {events.map((evt) => (
                          <label key={evt} className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                            <input
                              type="checkbox"
                              checked={form.events.includes(evt)}
                              onChange={() => toggleEvent(evt)}
                              className="rounded"
                            />
                            {evt}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="input-group">
                  <label className="input-label">Signing Secret (optional)</label>
                  <input
                    className="input"
                    type="password"
                    placeholder="Leave empty to skip HMAC signing"
                    value={form.secret}
                    onChange={(e) => setForm({ ...form, secret: e.target.value })}
                  />
                  <p className="input-hint">Used to sign payloads with HMAC-SHA256</p>
                </div>
                <div className="input-group flex items-center gap-3 pt-6">
                  <label className="flex items-center gap-2 text-sm text-ink-primary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      className="rounded"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update Webhook' : 'Create Webhook'}
                </button>
                <button type="button" className="btn-secondary" onClick={cancel}>
                  Cancel
                </button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Label</Th>
                  <Th>URL</Th>
                  <Th>Events</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {webhooks.map((wh) => (
                  <tr key={wh.id}>
                    <Td>{wh.label || '-'}</Td>
                    <Td>
                      <span className="font-mono text-xs" title={wh.webhookUrl}>
                        {wh.webhookUrl.length > 40 ? wh.webhookUrl.slice(0, 40) + '...' : wh.webhookUrl}
                      </span>
                    </Td>
                    <Td>
                      <Badge variant="brand">{wh.events?.length || 0} events</Badge>
                    </Td>
                    <Td>
                      <Badge variant={wh.active !== false ? 'success' : 'danger'}>
                        {wh.active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => testWebhook(wh)}
                          disabled={testing === wh.id}
                          title="Send test payload"
                        >
                          <FaPaperPlane size={12} />
                        </button>
                        <button className="btn-secondary btn-sm" onClick={() => edit(wh)}>
                          <FaEdit size={12} />
                        </button>
                        <button className="btn-danger btn-sm" onClick={() => del(wh.id)}>
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
                {webhooks.length === 0 && (
                  <tr>
                    <Td colSpan="5" className="text-center text-ink-muted py-6">
                      No webhooks configured yet
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </CardBody>
      </Card>
    </div>
  );
}
